/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Keyboard: sheet compacto (form corto) envuelto en KeyboardAvoidingView para que
 * suba con el teclado, sin forzar flex:1 en el card (evita estirarlo a pantalla completa).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  KeyboardAvoidingView,
  type KeyboardAwareScrollViewRef,
} from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { Product } from '../../../catalog/domain-api';
import { formatUnitLabel } from '../../../catalog/domain-api';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ModalFormField } from '../../../shared/modals/ModalFormField'
import { ModalFormScroll } from '../../../shared/modals/ModalFormScroll'
import { ModalScreenHeader } from '../../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../../shared/modals/modalExperience.styles'
import { modalShellShared } from '../../../shared/modals/modalShellShared.styles';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';
import { ticketChrome } from '../../../shared/ticket/ticketChrome.styles';
import { CONTENT_WIDE_BREAKPOINT } from '../../../shared/layout/breakpoints';
import { formatGramsAsCartInput } from '../../../scale/domain/gramsToCartQuantity'
import { getBranchScaleConfig } from '../../../scale/infra/scaleConfigRepo'
import { useLiveScaleWeight } from '../../../scale/hooks/useLiveScaleWeight'
import type { BranchScaleConfig } from '../../../scale/domain/scaleConfig';

type Props = {
  visible: boolean;
  product: Product | null;
  onCancel: () => void;
  onConfirm: (quantity: number) => void;
  heading?: string;
  /** Sucursal para config de balanza (AsyncStorage). */
  branchId?: string | null;
  /** Flag + capability weight_uom. */
  scaleFeatureEnabled?: boolean;
};

export function PosWeightQuantityModal({
  visible,
  product,
  onCancel,
  onConfirm,
  heading = 'Cantidad por peso',
  branchId = null,
  scaleFeatureEnabled = false,
}: Props) {
  const [value, setValue] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [scaleConfig, setScaleConfig] = useState<BranchScaleConfig | null>(null);
  const uom = product?.unitOfMeasure ?? 'kg';
  const unitLabel = formatUnitLabel(uom);
  const insets = useSafeAreaInsets();
  const backdropPadding = useModalBackdropPadding();
  const { width: winW, height: winH } = useWindowDimensions();
  const isMobile = winW < CONTENT_WIDE_BREAKPOINT;
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);

  useEffect(() => {
    if (visible) {
      setValue('1');
      setError(null);
    }
  }, [visible, product?.id]);

  useEffect(() => {
    if (!visible || !scaleFeatureEnabled || !branchId) {
      setScaleConfig(null);
      return;
    }
    let cancelled = false;
    void getBranchScaleConfig(branchId).then((cfg) => {
      if (!cancelled) setScaleConfig(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, scaleFeatureEnabled, branchId]);

  const scaleActive = Boolean(visible && scaleFeatureEnabled && scaleConfig?.enabled);
  const { reading: liveReading, error: liveError } = useLiveScaleWeight({
    active: scaleActive,
    config: scaleConfig,
  });

  useEffect(() => {
    if (!scaleActive || !liveReading?.stable || liveReading.grams <= 0) return;
    const next = formatGramsAsCartInput(liveReading.grams, uom);
    if (next) {
      setValue(next);
      setError(null);
    }
  }, [scaleActive, liveReading?.grams, liveReading?.stable, uom]);

  const handleConfirm = () => {
    const qty = parseDecimalInput(value, { min: 0.0001, max: 9999 });
    if (qty == null) {
      setError('Ingresá una cantidad válida mayor a 0.');
      return;
    }
    onConfirm(qty);
    setValue('1');
    setError(null);
  };

  const parsedQty = parseDecimalInput(value, { min: 0.0001, max: 9999 });
  const canConfirm = parsedQty != null;

  const livePanel =
    scaleActive ? (
      <View style={styles.livePanel} testID="pos.scale.live-panel">
        <Text style={styles.liveTitle}>Peso en vivo</Text>
        {liveReading ? (
          <Text style={styles.liveValue} accessibilityLabel="Peso de balanza">
            {(liveReading.grams / 1000).toFixed(3).replace('.', ',')} kg
            {liveReading.stable ? ' · estable' : ' · midiendo…'}
          </Text>
        ) : (
          <Text style={styles.liveHint}>{liveError ?? 'Conectando con el agente…'}</Text>
        )}
        {liveReading && !liveReading.stable ? (
          <Text style={styles.liveHint}>Esperá a que se estabilice o escribí el peso a mano.</Text>
        ) : null}
        {liveError && liveReading ? <Text style={styles.liveError}>{liveError}</Text> : null}
        {liveReading && liveReading.stable ? (
          <TouchableOpacity
            style={styles.useScaleBtn}
            onPress={() => {
              const next = formatGramsAsCartInput(liveReading.grams, uom);
              if (next) {
                setValue(next);
                setError(null);
              }
            }}
            accessibilityRole="button"
            accessibilityLabel="Usar peso de balanza"
            testID="pos.scale.use-weight"
          >
            <Text style={styles.useScaleBtnText}>Usar peso de balanza</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    ) : null;

  const formFields = (
    <>
      {livePanel}
      <ModalFormField label={`Cantidad (${unitLabel})`} required>
        <NumericTextInput
          value={value}
          onChangeText={(v) => { setValue(v); setError(null); }}
          kind="decimalMoney"
          style={modalExperience.formInput}
          placeholder="0,5"
          placeholderTextColor={ticketColors.ink3}
          accessibilityLabel={`Cantidad en ${unitLabel}`}
          autoFocus={!scaleActive}
          testID="pos.weight.qty-input"
        />
        <Text style={{ color: ticketColors.ink3, fontSize: 12, marginTop: 4 }}>
          {scaleActive
            ? 'La balanza completa el peso cuando está estable. También podés escribirlo.'
            : 'Ingresá el peso/cantidad. Máx 9999.'}
        </Text>
        {error ? <Text style={{ color: ticketColors.bad, fontSize: 12, marginTop: 4 }}>{error}</Text> : null}
      </ModalFormField>
    </>
  );

  const footerActions = (
    <>
      <TouchableOpacity
        style={
          isMobile
            ? [ticketChrome.btnGhost, { flex: 1, backgroundColor: ticketColors.paper2 }]
            : [posUi.secondaryButton, posUi.modalButtonHalf]
        }
        onPress={onCancel}
        accessibilityRole="button"
        accessibilityLabel="Cancelar"
      >
        <Text style={isMobile ? [ticketChrome.btnGhostText, { color: ticketColors.ink }] : posUi.secondaryButtonText}>
          Cancelar
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={isMobile ? [ticketChrome.btnGold, { flex: 1 }] : [posUi.primaryButton, posUi.modalButtonHalf]}
        onPress={handleConfirm}
        disabled={!canConfirm}
        accessibilityRole="button"
        accessibilityLabel="Agregar"
      >
        <Text style={isMobile ? ticketChrome.btnGoldText : posUi.primaryButtonText}>Agregar</Text>
      </TouchableOpacity>
    </>
  );

  // ── Mobile: sheet compacto, KeyboardAvoidingView lo levanta con el teclado ──
  if (isMobile) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel} accessibilityViewIsModal>
        <View style={ticketChrome.sheetBackdrop}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={onCancel}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap} automaticOffset>
            <View
              style={[ticketChrome.sheetCard, { maxHeight: winH * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }]}
            >
              <View style={ticketChrome.grabber} />

              <View style={ticketChrome.sheetHeader}>
                <View style={styles.sheetHeaderText}>
                  <Text style={ticketChrome.sheetTitle}>{heading}</Text>
                  {product?.name ? (
                    <Text style={ticketChrome.sheetSubtitle} numberOfLines={2}>
                      {product.name}
                    </Text>
                  ) : null}
                </View>
                <TouchableOpacity
                  onPress={onCancel}
                  style={ticketChrome.sheetCloseBtn}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
                </TouchableOpacity>
              </View>

              <View style={[styles.sheetScroll, styles.sheetScrollContent]}>
                <View ref={scrollContentRef} collapsable={false}>
                  {formFields}
                </View>
              </View>

              <View style={ticketChrome.foot}>{footerActions}</View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel} accessibilityViewIsModal>
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            modalExperience.modalWidthStandard,
            { gap: spacing.sm, width: '100%', alignSelf: 'center', maxHeight: '92%' },
          ]}
        >
          <ModalScreenHeader title={heading} subtitle={product?.name} onClose={onCancel} />
          <ModalFormScroll
            ref={scrollRef}
            contentRef={scrollContentRef}
            contentContainerStyle={modalExperience.formPanel}
            keyboardBottomPadding={spacing.sm}
          >
            {formFields}
          </ModalFormScroll>
          <View style={[posUi.modalActions, modalExperience.footerActions]}>{footerActions}</View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 18,
    paddingBottom: spacing.md,
    gap: spacing.sm,
  },
  livePanel: {
    borderWidth: 1,
    borderColor: ticketColors.border,
    borderRadius: 10,
    padding: spacing.sm,
    gap: 4,
    backgroundColor: ticketColors.paper2,
    marginBottom: spacing.sm,
  },
  liveTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  liveValue: {
    fontSize: 22,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  liveHint: {
    fontSize: 12,
    color: ticketColors.ink3,
  },
  liveError: {
    fontSize: 12,
    color: ticketColors.bad,
  },
  useScaleBtn: {
    marginTop: 6,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: ticketColors.ink,
  },
  useScaleBtnText: {
    color: ticketColors.paper,
    fontWeight: '600',
    fontSize: 13,
  },
});
